import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Leaf,
  TreePine,
  Sprout,
  Droplets,
  Sun,
  Flower,
  ArrowRight,
  Check,
  Shield,
  Star,
  Zap,
  Sparkles,
} from 'lucide-react'
import { useNavigate } from 'react-router'
import ImagePlaceholder from '../../utils/ImagePlaceholder'
import { ImageWithFallback } from '../../utils/ImageWithFallback'

import service1 from '../../assets/service/service-1.jpg'
import service2 from '../../assets/service/service-2.jpg'
import service3 from '../../assets/service/service-3.jpg'
import service4 from '../../assets/service/service-4.jpg'
import service5 from '../../assets/service/service-5.jpg'
import service6 from '../../assets/service/service-6.jpg'

export function ServicesComponent2() {
  const navigate = useNavigate()
  const services = [
    {
      icon: <Leaf className="w-8 h-8" />,
      title: 'Lawn Care & Maintenance',
      description:
        'Complete lawn care including mowing, edging, fertilizing, and seasonal treatments.',
      features: [
        'Weekly/Bi-weekly mowing',
        'Fertilization programs',
        'Weed control',
      ],
      image: service1,
      price: 'Starting at $75/visit',
      popular: true,
    },
    {
      icon: <Flower className="w-8 h-8" />,
      title: 'Garden Design & Installation',
      description:
        'Custom garden designs that reflect your style and complement your property.',
      features: [
        '3D design concepts',
        'Plant selection',
        'Installation services',
      ],
      image:
        service2,
      price: 'From $2,500',
      popular: false,
    },
    {
      icon: <TreePine className="w-8 h-8" />,
      title: 'Tree & Shrub Services',
      description:
        'Professional tree care including pruning, removal, and health assessments.',
      features: [
        'Tree pruning & trimming',
        'Disease treatment',
        'Stump grinding',
      ],
      image:
        service3,
      price: 'Quote on request',
      popular: false,
    },
    {
      icon: <Droplets className="w-8 h-8" />,
      title: 'Irrigation Systems',
      description:
        'Efficient watering solutions to keep your landscape healthy year-round.',
      features: [
        'Sprinkler installation',
        'Drip irrigation',
        'Smart controllers',
      ],
      image:
        service4,
      price: 'From $1,200',
      popular: false,
    },
    {
      icon: <Sun className="w-8 h-8" />,
      title: 'Hardscaping',
      description:
        'Beautiful patios, walkways, and outdoor living spaces that last.',
      features: ['Patio installation', 'Walkway design', 'Retaining walls'],
      image:
        service5,
      price: 'From $5,000',
      popular: false,
    },
    {
      icon: <Sprout className="w-8 h-8" />,
      title: 'Seasonal Cleanup',
      description:
        'Comprehensive seasonal services to prepare your landscape for every season.',
      features: ['Spring cleanup', 'Fall leaf removal', 'Mulching services'],
      image:
        service6,
      price: 'Starting at $150',
      popular: false,
    },
  ]

  const benefits = [
    {
      icon: <Zap className="w-6 h-6 text-green-600" />,
      title: 'Quick & Easy Process',
      description:
        'Get your project started with a simple, no-obligation consultation and transparent quotes.',
    },
    {
      icon: <Star className="w-6 h-6 text-green-600" />,
      title: 'Expert Craftsmanship',
      description:
        'Our certified professionals use the best materials and techniques for a perfect finish every time.',
    },
    {
      icon: <Shield className="w-6 h-6 text-green-600" />,
      title: 'Reliable & Guaranteed',
      description:
        'Enjoy peace of mind with our fully insured services and a 100% satisfaction guarantee.',
    },
  ]

  const fadeInVariants = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  }

  return (
    <section id="services" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial="initial"
          whileInView="animate"
          variants={fadeInVariants}
          viewport={{ once: true }}
        >
          <Badge className="bg-green-100 text-green-800 px-4 py-1.5 mb-4">
            <Sparkles className="w-4 h-4 mr-2" /> Our Landscaping Services
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            Transforming Your Outdoors
          </h2>
          <p className="text-md md:text-xl text-gray-600 max-w-4xl mx-auto">
            Explore our comprehensive range of services, designed to bring your
            vision to life and maintain the beauty of your property year-round.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial="initial"
              whileInView="animate"
              variants={{
                initial: { opacity: 0, scale: 0.95 },
                animate: {
                  opacity: 1,
                  scale: 1,
                  transition: { duration: 0.6, delay: index * 0.1 },
                },
              }}
              viewport={{ once: true }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="group"
            >
              <Card className="h-full overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 bg-white">
                <div className="relative">
                  <ImageWithFallback
                    src={service.image}
                    alt={service.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* <ImagePlaceholder width={384} height={192} /> */}
                  <div className="absolute top-0 right-0 p-4">
                    {service.popular && (
                      <Badge className="bg-green-600 text-white animate-pulse">
                        Most Popular
                      </Badge>
                    )}
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">
                        {service.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {service.description}
                      </p>
                    </div>
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
                      {service.icon}
                    </div>
                  </div>

                  <div className="mb-6 space-y-2">
                    {service.features.map((feature, i) => (
                      <div
                        key={i}
                        className="flex items-center text-sm text-gray-700"
                      >
                        <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div>
                      <span className="text-lg font-bold text-green-600">
                        {service.price}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-green-600 hover:bg-green-500/10"
                      onClick={() => navigate('/services/1')}
                    >
                      Details
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-8 text-center px-4 sm:px-0">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial="initial"
              whileInView="animate"
              variants={fadeInVariants}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
            >
              <div className="mb-4 flex justify-center">{benefit.icon}</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {benefit.title}
              </h3>
              <p className="text-gray-600">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
